import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/mocks/movies";

export default function HeroCarousel({ items }: { items: Movie[] }) {
  const [index, setIndex] = useState(0);
  const timer = useRef<number | null>(null);
  const touchStart = useRef(0);

  const go = (next: number) => {
    setIndex((next + items.length) % items.length);
  };

  useEffect(() => {
    timer.current = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 5500);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [items.length]);

  const current = items[index];
  if (!current) return null;

  return (
    <section className="relative w-full h-[480px] md:h-[600px] overflow-hidden bg-primary-950">
      <div
        className="absolute inset-0"
        onTouchStart={(e) => {
          touchStart.current = e.touches[0].clientX;
        }}
        onTouchEnd={(e) => {
          const delta = e.changedTouches[0].clientX - touchStart.current;
          if (delta > 50) go(index - 1);
          if (delta < -50) go(index + 1);
        }}
      >
        {items.map((item, i) => (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-700 ${i === index ? "opacity-100" : "opacity-0"}`}
          >
            <img
              src={item.backdrop}
              alt={`${item.title}剧照`}
              title={`${item.title} - 一本道影视大全`}
              className="w-full h-full object-cover object-top animate-kenburns"
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/70 to-primary-950/20"></div>
      </div>

      <div className="relative z-10 w-full h-full px-4 md:px-8 flex items-center">
        <div className="max-w-2xl w-full">
          <div className="flex items-center gap-2 mb-4 animate-fade-up">
            <span className="px-3 py-1 rounded-full bg-accent-500 text-primary-950 text-xs font-bold">
              今日焦点
            </span>
            <span className="px-3 py-1 rounded-full bg-background-50/15 text-background-50 text-xs font-medium border border-background-50/25">
              {current.type}
            </span>
            <span className="px-3 py-1 rounded-full bg-background-50/15 text-background-50 text-xs font-medium border border-background-50/25">
              {current.region}
            </span>
          </div>
          <h1 className="font-heading text-3xl md:text-5xl font-black text-background-50 leading-tight animate-fade-up">
            {current.title}
          </h1>
          <div className="flex items-center gap-4 mt-4 text-background-100 animate-fade-up">
            <span className="flex items-center gap-1 text-accent-400 font-bold">
              <i className="ri-star-fill"></i>
              {current.rating.toFixed(1)}
            </span>
            <span className="text-sm">{current.year}</span>
            <span className="text-sm">{current.genres.join(" / ")}</span>
            <span className="text-sm">{current.duration}</span>
          </div>
          <p className="mt-4 text-sm md:text-base text-background-200 leading-relaxed max-w-xl line-clamp-3 animate-fade-up">
            {current.summary}
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-7 animate-fade-up">
            <Link
              to={`/detail/${current.id}`}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-accent-500 text-primary-950 text-sm font-bold hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-fill text-lg"></i>
              查看详情
            </Link>
            <Link
              to="/category"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-background-50/10 text-background-50 text-sm font-semibold border border-background-50/30 hover:bg-background-50/20 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-compass-3-line text-lg"></i>
              逛影视库
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 right-4 md:right-8 z-10 flex items-center gap-3">
        <button
          type="button"
          aria-label="上一个"
          onClick={() => go(index - 1)}
          className="w-9 h-9 rounded-full bg-background-50/15 border border-background-50/30 text-background-50 flex items-center justify-center hover:bg-background-50/25 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>
        <div className="flex items-center gap-2">
          {items.map((item, i) => (
            <button
              key={item.id}
              type="button"
              aria-label={`切换到${item.title}`}
              onClick={() => setIndex(i)}
              className={`h-2 rounded-full transition-all cursor-pointer ${
                i === index ? "w-7 bg-accent-500" : "w-2 bg-background-50/40"
              }`}
            />
          ))}
        </div>
        <button
          type="button"
          aria-label="下一个"
          onClick={() => go(index + 1)}
          className="w-9 h-9 rounded-full bg-background-50/15 border border-background-50/30 text-background-50 flex items-center justify-center hover:bg-background-50/25 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-xl"></i>
        </button>
      </div>
    </section>
  );
}